<template>
    <kiosk-layout formContainer>
        <template v-slot:title>
            <span class="inline-flex items-center gap-3">
                <font-awesome-icon widthAuto :icon="faQrcode" class="text-base"></font-awesome-icon>
                <span>{{ editing ? 'Edit Link' : 'New Link' }}</span>
            </span>
        </template>

        <page-loading v-if="loading"></page-loading>

        <div v-else-if="loadError" class="mx-auto w-full max-w-(--breakpoint-sm)" data-test="load-error">
            <vx-alert color="danger" :closeable="false">{{ loadError }}</vx-alert>

            <vx-button href="/kiosk/inbound-text/links" color="muted-dark" size="lg" class="mt-4">
                Back to Links
            </vx-button>
        </div>

        <form-wrapper v-else :sections="[{ id: 'details', label: 'Details' }]">
            <vx-expandable-card id="details">
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div>
                            <vx-label size="md">Name</vx-label>
                            <vx-input
                                v-model="data.name"
                                size="lg"
                                maxlength="80"
                                placeholder="Name"
                                hint="A helpful name for your link to keep you organized."
                                data-test="name-input"
                                :error="displayFirstError(validation.name)"
                            />
                        </div>

                        <div>
                            <vx-label size="md">Phone</vx-label>
                            <vx-input
                                v-model="data.phone"
                                size="lg"
                                placeholder="+18001234567"
                                hint="The phone number you'd like your users to send an SMS message to."
                                data-test="phone-input"
                                :error="displayFirstError(validation.phone)"
                            />
                        </div>

                        <div>
                            <vx-label size="md">SMS Body</vx-label>
                            <vx-input
                                v-model="data.body"
                                size="lg"
                                placeholder="Type your SMS message here..."
                                :rows="4"
                                :maxlength="800"
                                :counter="true"
                                hint="This is the text that will be pre-populated in your user's message."
                                data-test="body-input"
                                :error="displayFirstError(validation.body)"
                            />
                        </div>

                        <div>
                            <vx-label size="md">Fallback URL</vx-label>
                            <vx-input
                                v-model="data.fallback_url"
                                size="lg"
                                maxlength="255"
                                placeholder="https://example.com"
                                hint="This URL will be used instead of creating an SMS on desktops or unsupported devices."
                                data-test="fallback-url-input"
                                :error="displayFirstError(validation.fallback_url)"
                            />
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-sticky-footer>
                <vx-button href="/kiosk/inbound-text/links" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="processing"
                >
                    {{ editing ? 'Update Link' : 'Create Link' }}
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faQrcode } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxLabel, VxStickyFooter } from '@voxie/frontend-components';
import { maxLength, minLength, required } from '@vuelidate/validators';
import { isAxiosError } from 'axios';
import { ref } from 'vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { adminInboundTextClient } from '~/services';
import type { InboundTextLinkPayload } from '~/services/adminInboundTextClient.types';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { displayFirstError } from '~/utils/validation';

const props = defineProps<{ linkId?: number }>();

const editing = undefined !== props.linkId;

const data = ref<InboundTextLinkPayload>({ name: '', phone: '', body: '', fallback_url: '' });
const loading = ref(editing);
const loadError = ref('');
const savedLinkId = ref(props.linkId);

const { submit, processing, validation } = useFormSubmission({
    data,
    validation: {
        name: { required, maxLength: maxLength(80) },
        phone: { required },
        body: { required, minLength: minLength(4), maxLength: maxLength(800) },
        fallback_url: { required, maxLength: maxLength(255) },
    },
    submit: async () => {
        if (undefined !== props.linkId) {
            return adminInboundTextClient.updateLink(props.linkId, data.value);
        }

        const response = await adminInboundTextClient.createLink(data.value);
        savedLinkId.value = response.data.data.id;

        return response;
    },
    notification: () => ({
        message: editing ? 'Link updated' : 'Link created',
        redirect: `/kiosk/inbound-text/links/${savedLinkId.value}`,
    }),
    preserveScroll: false,
});

if (undefined !== props.linkId) {
    adminInboundTextClient
        .getLink(props.linkId)
        .then((response) => {
            const { name, phone, body, fallback_url } = response.data.data;
            data.value = { name, phone, body, fallback_url };
        })
        .catch((error) => {
            loadError.value =
                isAxiosError(error) && HTTP_NOT_FOUND === error.response?.status
                    ? 'Link not found.'
                    : 'Failed to load link. Please try again.';
        })
        .finally(() => {
            loading.value = false;
        });
}
</script>
